import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTheme } from '@mui/material/styles';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../services/firebase';
import SimpleNotesInput from '../../UI/SimpleNotesInput';
import { useAsyncForm } from '../../../hooks/useAsyncForm';
import CategoryCreationModal from '../CategoryCreationModal';
import colors from '../../../assets/theme/colors';
import DateTimeSection from './QuickCapture/DateTimeSection';
import IncidentContextPanel from './IncidentContextPanel';
import IncidentSeveritySelector from './IncidentSeveritySelector';
import IncidentTriggerPanel from './IncidentTriggerPanel';
import { useIncidentTimelineContext } from './context/useIncidentTimelineContext';
import { useIncidentCaptureFormState } from './useIncidentCaptureFormState';
import { useIncidentCaptureActions } from './useIncidentCaptureActions';
import { getAdaptiveResponseQuickLogConfig } from '../shared/adaptiveQuickLogConfig';

const OtherIncidentCapture = ({
  childId,
  childName,
  onSaved,
  onCategoryCreated,
  isCustomCategory = false,
  customCategoryType = null,
  incidentTypeOverride = null,
  mode = 'create',
  initialData = null,
  entryId = null,
  open = false,
  quickLogConfig = null,
}) => {
  const theme = useTheme();
  const [user] = useAuthState(auth);
  const adaptiveConfig = quickLogConfig || getAdaptiveResponseQuickLogConfig();
  const severityPresets = adaptiveConfig.severityPresets;
  const triggerGroups = adaptiveConfig.triggerGroups;

  const incidentForm = useAsyncForm({
    validate: ({ incidentName: inputName, isCustomCategory: custom }) => {
      if (!custom && !incidentTypeOverride && !inputName?.trim()) {
        throw new Error('Please enter what happened');
      }
    },
  });

  const {
    incidentName,
    setIncidentName,
    severity,
    setSeverity,
    notes,
    setNotes,
    setIncidentDateTime,
    incidentTime,
    selectedTriggers,
    setSelectedTriggers,
    isEditMode,
  } = useIncidentCaptureFormState({
    mode,
    entryId,
    initialData,
    open,
    clearError: incidentForm.clearError,
  });
  const selectedSeverityPreset = severityPresets.find((preset) => preset.value === severity) || severityPresets[1];
  const {
    contextItems,
    patternInsight,
  } = useIncidentTimelineContext(childId, incidentTime);

  const semantic = theme.palette.semantic || colors.semantic;
  const behaviorPalette = semantic.behavior || colors.semantic.behavior;
  const contextPalette = semantic.context || colors.semantic.context;
  const lineColor = semantic.line || colors.semantic.line;
  const lineSoftColor = semantic.line2 || colors.semantic.line2;
  const inkColor = semantic.ink || colors.semantic.ink;
  const ink2Color = semantic.ink2 || colors.semantic.ink2;
  const ink3Color = semantic.ink3 || colors.semantic.ink3;
  const surfaceColor = theme.palette.background.paper || semantic.bg || '#FFFFFF';
  const surfaceSoftColor = colors.landing.surfaceSoft || semantic.card || '#F8FAFC';

  const canSave = isCustomCategory || Boolean(incidentTypeOverride) || incidentName.trim().length > 0;
  const {
    showCategoryModal,
    categorySuggestion,
    handleSave,
    handleCategoryCreated,
    handleCategoryModalClose,
  } = useIncidentCaptureActions({
    childId,
    childName,
    onSaved,
    onCategoryCreated,
    isCustomCategory,
    customCategoryType,
    incidentTypeOverride,
    entryId,
    initialData,
    isEditMode,
    incidentForm,
    incidentTime,
    incidentName,
    severity,
    notes,
    selectedTriggers,
    contextItems,
    patternInsight,
    user,
    triggerGroups,
    quickLogConfig: adaptiveConfig,
  });

  const toggleTrigger = (triggerKey) => {
    setSelectedTriggers((current) =>
      current.includes(triggerKey)
        ? current.filter((item) => item !== triggerKey)
        : [...current, triggerKey]
    );
  };

  const saveLabel = incidentForm.loading ? 'Saving...' : adaptiveConfig.saveLabel;

  return (
    <Box
      sx={{
        minHeight: '100%',
        bgcolor: semantic.bg,
        px: { xs: 1.25, sm: 2 },
        py: { xs: 1.25, sm: 2 },
      }}
    >
      {incidentForm.error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => incidentForm.clearError()}
        >
          {incidentForm.error}
        </Alert>
      )}

      <Stack spacing={2}>
        <DateTimeSection value={incidentTime} onChange={setIncidentDateTime} />

        <SimpleNotesInput
          label={adaptiveConfig.inputLabel}
          hintText="Tip: use your phone mic to dictate notes."
          value={incidentName}
          onDataChange={(data) => setIncidentName(data?.text || '')}
          placeholder={adaptiveConfig.inputPlaceholder}
          minRows={4}
          maxRows={8}
          textFieldSx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              bgcolor: surfaceColor,
              minHeight: 80,
              alignItems: 'flex-start',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: lineColor,
              borderWidth: '1.5px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: lineSoftColor,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: behaviorPalette.ink,
            },
            '& .MuiInputBase-input': {
              fontSize: '0.906rem',
              lineHeight: 1.45,
              color: inkColor,
            },
          }}
        />

        <IncidentSeveritySelector
          severity={severity}
          onChange={setSeverity}
          presets={severityPresets}
          selectedPreset={selectedSeverityPreset}
          label={adaptiveConfig.severityLabel}
          inkColor={inkColor}
          ink2Color={ink2Color}
          lineColor={lineColor}
          surfaceColor={surfaceColor}
        />

        <IncidentContextPanel contextItems={contextItems} contextPalette={contextPalette} />

        <IncidentTriggerPanel
          behaviorPalette={behaviorPalette}
          surfaceColor={surfaceColor}
          inkColor={inkColor}
          notes={notes}
          setNotes={setNotes}
          selectedTriggers={selectedTriggers}
          toggleTrigger={toggleTrigger}
          triggerGroups={triggerGroups}
          title={adaptiveConfig.triggerTitle}
          helperText={adaptiveConfig.triggerHelper}
          notesLabel={adaptiveConfig.triggerNotesLabel}
          notesPlaceholder={adaptiveConfig.triggerNotesPlaceholder}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', pt: 0.75 }}>
          <Button
            variant="outlined"
            onClick={onSaved}
            sx={{
              flex: 1,
              minHeight: 52,
              borderRadius: 999,
              borderWidth: '1.5px',
              borderColor: lineColor,
              bgcolor: surfaceColor,
              color: ink2Color,
              fontWeight: 650,
              fontSize: 14,
              '&:hover': {
                borderColor: lineSoftColor,
                bgcolor: surfaceSoftColor,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave || incidentForm.loading}
            startIcon={incidentForm.loading ? <CircularProgress size={20} color="inherit" /> : <CheckRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              flex: 1.55,
              minHeight: 52,
              borderRadius: 999,
              bgcolor: semantic.sageDeep,
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(111, 165, 148, 0.3)',
              fontWeight: 700,
              fontSize: 15,
              '&:hover': {
                bgcolor: semantic.sageDeep,
                boxShadow: '0 4px 12px rgba(111, 165, 148, 0.36)',
              },
              '&.Mui-disabled': {
                bgcolor: lineColor,
                opacity: 0.65,
                color: ink3Color,
                boxShadow: 'none',
              },
            }}
          >
            {saveLabel}
          </Button>
        </Box>
      </Stack>

      <CategoryCreationModal
        open={showCategoryModal}
        onClose={handleCategoryModalClose}
        childId={childId}
        suggestion={categorySuggestion}
        onCategoryCreated={handleCategoryCreated}
      />
    </Box>
  );
};

export default OtherIncidentCapture;
